import { useState, type FC } from "react";

import { ArrowRight, Loader2, Info } from "lucide-react";

import { Button } from "@/shared/components/ui/button";
import { FieldError } from "@/shared/components/ui/FieldError";
import { Input } from "@/shared/components/ui/input";
import { PhoneNumberInput } from "@/shared/components/ui/phone-number-input";
import {
  Tooltip,
  TooltipContent,
  TooltipInfoTrigger,
  TooltipProvider,
  TooltipTrigger,
} from "@/shared/components/ui/tooltip";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import { US_STATES } from "@/shared/constants/constants";
import { SecurityNote } from "@/shared/components/ui/security-note";
import { WHY_WE_ASK_TITLE } from "@/shared/constants/whyWeAsk";

export interface AttorneyProfile {
  firstName: string;
  lastName: string;
  phone: string;
  stateOfPractice: string;
  barNumber: string;
  firmName: string;
}

interface ProfileSetupStepProps {
  onSubmit: (profile: AttorneyProfile) => Promise<void>;
  isLoading: boolean;
  error: string | null;
  prefillFirstName?: string;
  prefillLastName?: string;
  prefillPhone?: string;
}

interface FieldErrors {
  firstName?: string;
  lastName?: string;
  phone?: string;
  stateOfPractice?: string;
  barNumber?: string;
  firmName?: string;
}

export const ProfileSetupStep: FC<ProfileSetupStepProps> = ({
  onSubmit,
  isLoading,
  error,
  prefillFirstName,
  prefillLastName,
  prefillPhone,
}) => {
  const [firstName, setFirstName] = useState(prefillFirstName ?? "");
  const [lastName, setLastName] = useState(prefillLastName ?? "");
  const [phone, setPhone] = useState(prefillPhone ?? "");
  const [stateOfPractice, setStateOfPractice] = useState("");
  const [barNumber, setBarNumber] = useState("");
  const [firmName, setFirmName] = useState("");
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});

  const validate = (): boolean => {
    const errors: FieldErrors = {};
    if (!firstName.trim()) errors.firstName = "First name is required";
    if (!lastName.trim()) errors.lastName = "Last name is required";
    if (!phone.trim()) errors.phone = "Phone number is required";
    else if (phone.replace(/\D/g, "").length < 10)
      errors.phone = "Enter a valid 10-digit phone number";
    if (!stateOfPractice) errors.stateOfPractice = "Select a state";
    if (!firmName.trim()) errors.firmName = "Firm name is required";
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    await onSubmit({ firstName, lastName, phone, stateOfPractice, barNumber, firmName });
  };

  return (
    <div className="flex w-full flex-col gap-10">
      <div className="flex flex-col items-center gap-3 text-center">
        <h2>Set up your profile</h2>
        <p className="text-sm text-secondary">
          A few last details and you&apos;re ready to invite clients
        </p>
      </div>

      <div className="flex w-full flex-col gap-6">
        <div className="grid grid-cols-2 gap-3">
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-primary">First name</label>
            <Input
              placeholder="First name"
              value={firstName}
              onChange={(e) => {
                setFirstName(e.target.value);
                setFieldErrors((prev) => ({ ...prev, firstName: undefined }));
              }}
              className={fieldErrors.firstName ? "border-destructive" : ""}
            />
            <FieldError message={fieldErrors.firstName} />
          </div>
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-primary">Last name</label>
            <Input
              placeholder="Last name"
              value={lastName}
              onChange={(e) => {
                setLastName(e.target.value);
                setFieldErrors((prev) => ({ ...prev, lastName: undefined }));
              }}
              className={fieldErrors.lastName ? "border-destructive" : ""}
            />
            <FieldError message={fieldErrors.lastName} />
          </div>
        </div>

        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-medium text-primary">Business phone</label>
          <PhoneNumberInput
            value={phone}
            onChange={(value) => {
              setPhone(value);
              setFieldErrors((prev) => ({ ...prev, phone: undefined }));
            }}
            className={fieldErrors.phone ? "border-destructive" : ""}
          />
          <FieldError message={fieldErrors.phone} />
        </div>

        <div className="flex flex-col gap-1.5">
          <div className="flex items-center gap-1.5">
            <label className="text-sm font-medium text-primary">Primary state of practice</label>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Info className="h-3.5 w-3.5 cursor-help text-secondary" />
                </TooltipTrigger>
                <TooltipContent>
                  If you're licensed in more than one state, choose the state where you handle the
                  majority of your matters.
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          <Select
            value={stateOfPractice}
            onValueChange={(value) => {
              setStateOfPractice(value);
              setFieldErrors((prev) => ({ ...prev, stateOfPractice: undefined }));
            }}
          >
            <SelectTrigger className={fieldErrors.stateOfPractice ? "border-destructive" : ""}>
              <SelectValue placeholder="Select state" />
            </SelectTrigger>
            <SelectContent>
              {US_STATES.map((state) => (
                <SelectItem key={state.value} value={state.value}>
                  {state.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <FieldError message={fieldErrors.stateOfPractice} />
        </div>

        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-medium text-primary">
            Bar number / Registration number{" "}
            <span className="font-normal text-tertiary">(optional)</span>
            <TooltipProvider>
              <Tooltip>
                <TooltipInfoTrigger aria-label={WHY_WE_ASK_TITLE} className="ml-1 align-middle" />
                <TooltipContent title={WHY_WE_ASK_TITLE}>
                  {
                    "Elayne confirms each attorney's admission with the state bar before the account goes live. Clients you invite can rely on that check."
                  }
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </label>
          <Input
            placeholder="e.g. 123456"
            inputMode="numeric"
            value={barNumber}
            onChange={(e) => {
              const cleaned = e.target.value.replace(/[^0-9]/g, "");
              setBarNumber(cleaned);
              setFieldErrors((prev) => ({ ...prev, barNumber: undefined }));
            }}
            className={fieldErrors.barNumber ? "border-destructive" : ""}
          />
          <FieldError message={fieldErrors.barNumber} />
        </div>

        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-medium text-primary">Firm name</label>
          <Input
            placeholder="e.g. Doe & Associates LLP"
            value={firmName}
            onChange={(e) => {
              setFirmName(e.target.value);
              setFieldErrors((prev) => ({ ...prev, firmName: undefined }));
            }}
            className={fieldErrors.firmName ? "border-destructive" : ""}
          />
          <FieldError message={fieldErrors.firmName} />
        </div>

        <FieldError message={error} />

        <Button
          type="button"
          variant="brand"
          rounded={true}
          onClick={handleSubmit}
          disabled={isLoading}
          className="w-full"
        >
          {isLoading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <>
              Go to dashboard <ArrowRight className="h-4 w-4" />
            </>
          )}
        </Button>
      </div>

      <SecurityNote text="We verify every attorney to keep client estate records protected" />
    </div>
  );
};
